import { z } from 'zod'

/**
 * The date a medical service was delivered.
 *
 * ## Why this is a type and not a `string`
 *
 * Annex A states, three separate times — in the new-bill, upward-adjustment and
 * downward-adjustment stories — that a bill is validated against the officer's
 * entitlement **as at the date of the medical service, not the bill submission
 * date**. It is the single rule the correctness of this system turns on, and it
 * fails silently: an implementation that resolves entitlement "now" produces
 * plausible money on every same-day bill and wrong money on every back-dated or
 * adjusted one.
 *
 * A bare `string` parameter cannot stop a caller passing the submission date. A
 * branded type can, at compile time, and that is the entire justification for the
 * ceremony below. See `docs/design/domain-language.md` §1.
 */
declare const serviceDateBrand: unique symbol
declare const submissionDateBrand: unique symbol
declare const businessDateBrand: unique symbol

/** An ISO calendar date, `YYYY-MM-DD`, with no time and no zone. */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO calendar date, YYYY-MM-DD')
  .refine((value) => {
    // `new Date('2026-02-31')` does not throw — it rolls over to 3 March. A regex
    // alone therefore accepts dates that do not exist, and a rolled-over date would
    // silently select the wrong entitlement period.
    const [year, month, day] = value.split('-').map(Number) as [number, number, number]
    const parsed = new Date(Date.UTC(year, month - 1, day))
    return (
      parsed.getUTCFullYear() === year &&
      parsed.getUTCMonth() === month - 1 &&
      parsed.getUTCDate() === day
    )
  }, 'must be a real calendar date')

/**
 * The date of medical service. **This is the date entitlement is resolved on.**
 */
export const ServiceDate = isoDate.brand<typeof serviceDateBrand>()
export type ServiceDate = z.infer<typeof ServiceDate>

/**
 * The date a bill or adjustment was submitted.
 *
 * Carried because it is real, reportable and needed for grace-period arithmetic —
 * and given its own type precisely so it can never be passed where a
 * {@link ServiceDate} is expected.
 */
export const SubmissionDate = isoDate.brand<typeof submissionDateBrand>()
export type SubmissionDate = z.infer<typeof SubmissionDate>

/**
 * The counterparty's own cycle date for an exchanged file, as DECLARED in its
 * header.
 *
 * ⚠ **The third date, and the one most likely to be mistaken for the other
 * two.** It is a reconciliation aid and nothing else: it decides no entitlement,
 * no rule version and no grace period.
 *
 *  - It is not the service date. A file dated today routinely carries services
 *    delivered months ago — that is the normal case on this system, not the edge
 *    case, and resolving entitlement on it would produce plausible wrong money
 *    on every one of them.
 *  - It is not the submission date. The submission date is the date PRS RECEIVED
 *    the file, set by PRS, because a counterparty-supplied one is unverifiable
 *    and would let a provider backdate itself into a grace period it had missed
 *    (Annex A 45). The declared business date is stored beside it precisely so a
 *    divergence is visible rather than absorbed. The trade is real — a file
 *    delayed by a PRS outage gets a later submission date through no fault of the
 *    provider — so this is filed as a clarification, not assumed away.
 *
 * It lives in this file rather than beside the file-exchange types because
 * `isoDate` — including the refinement that rejects `2026-02-31`, which
 * `new Date()` silently rolls over to 3 March — is defined here and is
 * deliberately not exported. A second copy of that refinement is the
 * two-implementations-of-one-sentence failure this repository keeps recording.
 * Keeping all three brands together is also what makes the mutual exclusion
 * below expressible at all.
 */
export const BusinessDate = isoDate.brand<typeof businessDateBrand>()
export type BusinessDate = z.infer<typeof BusinessDate>

/**
 * Parse a service date from a raw string.
 *
 * ⚠ The conditional type is not decoration. A branded string is still assignable
 * to `string`, so a plain `(value: string)` signature accepts a `SubmissionDate`
 * without a cast — `serviceDate(bill.submissionDate)` would compile, validate
 * (it is a real date), and reach every lookup with nothing for a reviewer to
 * see. That is the exact laundering route the brands exist to close, and it
 * survived eight negative tests because all of them probed assignment rather
 * than the constructor.
 *
 * ⚠ `BusinessDate` is excluded for the same reason and it is the more likely
 * mistake of the two: the business date is the one date that is actually PRESENT
 * as a field in an inbound file, so `serviceDate(header.businessDate)` is the
 * line an implementer writes when the record's own service date is missing.
 */
export const serviceDate = <T extends string>(
  value: T & (T extends SubmissionDate | BusinessDate ? never : T),
): ServiceDate => ServiceDate.parse(value)

/** The mirror of the above: a `ServiceDate` must not launder into a submission date. */
export const submissionDate = <T extends string>(
  value: T & (T extends ServiceDate | BusinessDate ? never : T),
): SubmissionDate => SubmissionDate.parse(value)

/** And the third: neither of the deciding dates may launder into the reconciliation one. */
export const businessDate = <T extends string>(
  value: T & (T extends ServiceDate | SubmissionDate ? never : T),
): BusinessDate => BusinessDate.parse(value)

/**
 * A closed-open date window: `[from, until)`.
 *
 * Open-ended at the top is represented by `until: null` rather than a sentinel
 * date. A far-future sentinel invites arithmetic on a date that does not mean what
 * it says, and it eventually arrives.
 *
 * **Closed-open is deliberate.** With inclusive-inclusive windows, two consecutive
 * entitlement periods either overlap by a day or leave a one-day hole, and both
 * mistakes are invisible until a bill lands exactly on the boundary.
 */
declare const dateWindowBrand: unique symbol

export const DateWindow = z
  .object({ from: isoDate, until: isoDate.nullable() })
  .refine((w) => w.until === null || w.from < w.until, {
    message: 'window must start before it ends',
  })
  .brand<typeof dateWindowBrand>()
export type DateWindow = z.infer<typeof DateWindow>

/**
 * Build a validated date window.
 *
 * ⚠ The only way to make one. An earlier version exported the type without a
 * brand, so every window in the system was an unvalidated object literal and the
 * refinements above ran on nothing, ever — including the check that a window
 * starts before it ends. An inverted effective window makes a rule match nothing
 * and the bill falls through to an adjacent version or fails outright, which is
 * the failure `replay` exists to catch and could not see.
 */
export const dateWindow = (from: string, until: string | null): DateWindow =>
  DateWindow.parse({ from, until })

/**
 * Does `date` fall inside `window`? Start inclusive, end exclusive.
 *
 * ⚠ Takes a `ServiceDate`, not a `string`, and that is the point. This is the
 * primitive every entitlement and rule lookup delegates to, so a bare `string`
 * parameter here would leave the whole branded-date guarantee open at its base:
 * a caller holding a submission date could satisfy it without a cast and without
 * a reviewer seeing anything. If a window check against some other kind of date
 * is genuinely needed later, add a separate function for it deliberately rather
 * than widening this one.
 */
export function windowContains(window: DateWindow, date: ServiceDate): boolean {
  if (date < window.from) return false
  if (window.until === null) return true
  return date < window.until
}

/**
 * Whole days elapsed from the date of medical service to the date the bill was
 * submitted. Negative when the submission predates the service.
 *
 * ⚠ **This is the one function in the system that is ALLOWED to see both
 * dates**, and its signature is the control that keeps it that way: the
 * parameters are the two distinct brands, in a fixed order, so
 * `daysFromServiceToSubmission(bill.submissionDate, bill.serviceDate)` does not
 * compile. `prorate()` cannot reach it at all — `ProratableBill` has no
 * submission date — which is deliberate: the grace-period rule needs both dates
 * and the split rule needs exactly one, and mixing them in a single function is
 * how the wrong one gets used.
 *
 * ⚠ Computed from the ISO components through `Date.UTC`, never from
 * `new Date(string) - new Date(string)` on a local clock and never through any
 * float arithmetic on a duration. Both operands land on UTC midnight, so the
 * difference is an exact multiple of 86,400,000 ms and the division is exact —
 * there is no DST hour to lose, because these values carry no zone at all. The
 * result is asserted to be a safe integer rather than assumed to be one.
 *
 * A negative result is returned rather than refused. It means the source data
 * says a bill was submitted before the service happened, which is a defect in
 * the feed and an answer the caller must be able to see and act on — not
 * something to clamp to zero here, where it would disappear.
 */
export function daysFromServiceToSubmission(service: ServiceDate, submission: SubmissionDate): number {
  const utcMidnight = (value: string): number => {
    const [year, month, day] = value.split('-').map(Number) as [number, number, number]
    return Date.UTC(year, month - 1, day)
  }
  const elapsed = (utcMidnight(submission) - utcMidnight(service)) / 86_400_000
  if (!Number.isSafeInteger(elapsed)) {
    // Unreachable while both operands are `isoDate`-validated calendar dates at
    // UTC midnight. Asserted anyway: a fractional or non-finite day count
    // reaching a grace-period comparison would compare, silently, and the
    // comparison decides whether a government medical bill is accepted.
    throw new RangeError(
      `days between ${service} and ${submission} came out as ${elapsed}, which is not a whole ` +
        `number of days. Both operands are validated calendar dates, so this is a defect in this ` +
        `function, not in its inputs.`,
    )
  }
  return elapsed
}
